<template>
    <BasePanel
        :headline="$t('lang_LABEL_CONTRACTS_PANEL_CAPTION_COSTS') + ' (' + $t('lang_GENERIC_CAPTION_BETA_DO_NOT_USE') + ')'"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :loading="isLoading"
                :items-per-page="300"
                :page.sync="currentPage"
                :server-items-length="totalRecords"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3 pl-4">
                        <v-spacer></v-spacer>
                        <v-col 
                            cols="3"
                            align-self="end"
                        >
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
                <template v-slot:[`item.costGroup`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getCostGroup(item.costGroup)"
                            :alvqa="alvqa + '-list-row-text-cost-group'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.date`]="{ item }">
                    <div 
                        class="text-right text-no-wrap"
                        :alvqa="alvqa + '-list-row-text-date'"
                    >
                        {{ item.date !== null && item.date !== "0000-00-00" ? $d(new Date(item.date), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.recipient`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.recipient !== null ? item.recipient.gemaName : ''"
                            :alvqa="alvqa + '-list-row-text-recipient'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.description`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.description"
                            :alvqa="alvqa + '-list-row-text-description'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <div 
                        class="text-right text-no-wrap"
                        :alvqa="alvqa + '-list-row-text-amount'"
                    >                        
                        {{ $alvPlugin.formatMoney(item.amount) }}
                    </div>
                </template>
                <template v-slot:[`item.documentNumber`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.documentNumber"
                            :alvqa="alvqa + '-list-row-text-document-number'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.country`]="{ item }">
                    <div class="text-no-wrap">
                        <BaseTooltip>
                            <template v-slot:content>
                                <div 
                                    :alvqa="alvqa + '-list-row-text-country'"
                                >{{ $alvPlugin.getTranslatedCountryName(item.country, true) }}</div>
                            </template>
                            <template v-slot:text>{{ item.country }}</template>
                        </BaseTooltip>
                    </div>
                </template>
                <template v-slot:[`item.paidOn`]="{ item }">
                    <div 
                        class="text-right text-no-wrap"
                        :alvqa="alvqa + '-list-row-text-paid-on'"
                    >
                        {{ item.paidOn !== null ? $d(new Date(item.paidOn), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.accountingPeriod`]="{ item }">
                    <div 
                        class="text-right text-no-wrap"
                        :alvqa="alvqa + '-list-row-text-accounting-period'"
                    >
                        {{ $alvPlugin.displayPeriod(item.accountingPeriod) }}
                    </div>
                </template>
                <template v-slot:[`item.split`]="{ item }">
                    <div 
                        class="text-right text-no-wrap"
                        :alvqa="alvqa + '-list-row-text-split'"
                    >                        
                        {{ $alvPlugin.getNumberLocalization(item.split, 'number') }} 
                    </div>
                </template>
                <template v-slot:[`item.assignToWork`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getAssignToWork(item.assignToWork)"
                            :alvqa="alvqa + '-list-row-text-assign-to-work'"
                        />
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
            </v-data-table>
            <v-pagination   
                v-if="numberOfPages > 1"
                total-visible="10" 
                v-model="currentPage" 
                :length="numberOfPages"
                color="pagination"
                @input="nextPage()"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import { DropdownPublishingContractsCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import BasePanel from '@/components/_base/BasePanel'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'

export default {
    name: "PublishingContractsCostsEditPanel",
    components: {
        BasePanel,
        BaseTooltip,
        BaseButton,
        BaseIcon
    },
    data() {
        return {
            alvqa: 'contracts-publishing-edit-form-panel-costs'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_COST_GROUP"), 
                    align: "left", 
                    value: "costGroup",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-cost-group'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_DATE"), 
                    align: "right", 
                    value: "date",
                    width: "100px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-date'
                },                
                {
                    text: this.$t("lang_GENERIC_CAPTION_RECIPIENT"), 
                    align: "left", 
                    value: "recipient",
                    width: "120px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-recipient'
                },
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    width: "120px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-description'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_AMOUNT") + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "amount",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-amount'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_DOCUMENT_NUMBER_SHORT'), 
                    align: "left", 
                    value: "documentNumber",
                    width: "100px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-document-number'
                },
                {
                    text: this.$t('lang_GENERAL_CAPTION_COUNTRY'), 
                    value: 'country',
                    align: 'left', 
                    class: 'text-no-wrap',
                    width: '110px',
                    sortable: false,
                    alvqa: this.alvqa + '-header-country'
                },
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_PAID_ON"), 
                    align: "right", 
                    value: "paidOn",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-paid-on'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_PERIOD'), 
                    align: "right", 
                    value: "accountingPeriod",
                    width: "130px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-accounting-period'
                },
                {
                    text: this.$t("lang_GENERAL_CAPTION_SPLIT") + ' %', 
                    align: "right", 
                    value: "split",
                    class: 'text-no-wrap',
                    width: "80px",
                    sortable: false,
                    alvqa: this.alvqa + '-header-split'
                }, 
                {
                    text: this.$t('lang_GENERIC_WORKS_SINGULAR'), 
                    align: "left", 
                    value: "assignToWork",
                    width: "100px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-assign-to-work'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },

        listItems() { return this.$store.getters.getPublishingContractsCosts },
        currentPage: {
            get() { return this.$store.getters.getPublishingContractsCostsCurrentPage },
            set(page) { this.$store.commit('setPublishingContractsCostsCurrentPage', page)}
        },
        numberOfPages() { return this.$store.getters.getPublishingContractsCostsNumberOfPages },
        totalRecords() { return this.$store.getters.getPublishingContractsCostsTotalRecords },
        isLoading() { return this.$store.getters.getIsLoading },
    },
    methods: {
        getCostGroup(costGroupValue) {
            let types = DropdownPublishingContractsCostGroupTypes
            if(types !== undefined) {
                let match = types.find(type => type.value === costGroupValue)
                if(match !== undefined) return this.$t(match.text)
                else return ''
            } else return ''
        },
        getAssignToWork(assignToWork) {
            if(assignToWork !== null) {
                return assignToWork.title + ' (' + assignToWork.alvId + ')' 
            } else return ''
        },
        deleteRow(item) {
            this.$store.commit("setSelectedPublishingContractsCosts", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedPublishingContractsCosts')
            this.$store.commit('setShowDeletionDialog', true)
        },
        addNewRow() {
            this.$store.commit('setShowPublishingContractsCostsEditingDialog', true)
            this.$store.dispatch('fetchAssignToWorkDropdown')
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))
            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultPublishingContractsCosts)) 
            this.$store.commit('setPublishingContractsCostsEditingDialogDetails', defaultValues)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedPublishingContractsCosts', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            detailsDeepCopy['amount'] = this.$n(detailsDeepCopy['amount'], 'number')
            detailsDeepCopy['split'] = this.$n(detailsDeepCopy['split'], 'number')
            if(detailsDeepCopy.recipient !== null) this.$store.commit('setPublishingContractsCostsRecipientOptions', [detailsDeepCopy.recipient])
            else this.$store.commit('setPublishingContractsCostsRecipientOptions', [])

            this.$store.commit('setPublishingContractsCostsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY"))
            this.$store.dispatch('fetchAssignToWorkDropdown')
            this.$store.commit('setShowPublishingContractsCostsEditingDialog', true)
        },
        async nextPage() {
            await this.$store.dispatch('fetchPublishingContractsCosts')
        },
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}
</script>
